/**
 * File: web/src/lib/upload.ts
 * ==========================
 *
 * Multipart upload helpers (browser → S3 direct upload)
 *
 * Purpose:
 * - Implement the client-side half of the multipart workflow:
 *   1) initiate upload (API)
 *   2) request pre-signed URLs (API)
 *   3) PUT parts directly to S3 (browser)
 *   4) complete or abort (API)
 *
 * When used:
 * - Upload UI logic (routes/Upload.tsx, components/UploadQueue.tsx).
 *
 * Learner notes:
 * - Direct-to-S3 uploads scale well and avoid pushing large payloads through Lambda/API Gateway.
 * - The API enforces auth/allowlist and issues signatures; the browser uploads bytes to S3.
 *
 * Gotchas:
 * - Presigned URLs expire; long uploads may need re-signing or smaller parts.
 * - Completion requires ETags for each part. Store them carefully.
 */

import type { AppConfig } from "./types";

export type UploadInit = { fileId: string; partSizeBytes: number };

/**
 * Initiate upload:
 * - Sends file metadata to backend so it can:
 *   - validate allowlist + file type
 *   - create S3 multipart upload
 *   - create a DynamoDB session record keyed by (email, fileId)
 */
export async function initiateUpload(
  config: AppConfig,
  token: string,
  file: File,
) {
  const res = await fetch(`${config.apiBaseUrl}/multipart/initiate`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },

    // Backend uses this to validate and record the upload.
    body: JSON.stringify({
      fileName: file.name,
      contentType: file.type,
      size: file.size,
    }),
  });

  const txt = await res.text();

  if (!res.ok) throw new Error(txt || `HTTP ${res.status}`);

  return JSON.parse(txt) as UploadInit;
}

/**
 * Get URL for a single part:
 * - partNumber is 1-based per S3 convention
 * - backend signs a URL scoped to:
 *   - specific uploadId + partNumber
 *   - a TTL (PART_URL_TTL_SECONDS)
 */
export async function getPartUrl(
  config: AppConfig,
  token: string,
  fileId: string,
  partNumber: number,
) {
  const res = await fetch(`${config.apiBaseUrl}/multipart/part-url`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ fileId, partNumber }),
  });
  const txt = await res.text();
  if (!res.ok) throw new Error(txt || `HTTP ${res.status}`);
  return JSON.parse(txt) as { url: string };
}

/**
 * PUT a part to S3 using XHR so we can track progress.
 *
 * Why XHR:
 * - Upload progress events are well-supported for XHR.
 * - fetch upload progress is still limited/inconsistent in many browsers.
 *
 * onDelta:
 * - delta bytes uploaded since last event (useful for aggregate progress across parts).
 */
export function putPart(
  url: string,
  blob: Blob,
  onDelta: (delta: number) => void,
): Promise<string> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    let last = 0;

    xhr.open("PUT", url, true);

    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) {
        const delta = e.loaded - last;
        last = e.loaded;
        onDelta(delta);
      }
    };

    xhr.onload = () => {
      // S3 returns an ETag header for the uploaded part (required for completion).
      if (xhr.status >= 200 && xhr.status < 300)
        resolve(xhr.getResponseHeader("ETag") || "");
      else reject(new Error(`S3 error ${xhr.status}`));
    };

    xhr.onerror = () => reject(new Error("Network error"));
    xhr.send(blob);
  });
}

/**
 * Complete upload:
 * - sends the ordered list of parts back to the backend
 * - backend calls S3 CompleteMultipartUpload and marks session COMPLETE in DynamoDB
 *
 * parts:
 * - must include { partNumber, eTag } for each part uploaded
 */
export async function completeUpload(
  config: AppConfig,
  token: string,
  fileId: string,
  parts: { partNumber: number; eTag: string }[],
) {
  const res = await fetch(`${config.apiBaseUrl}/multipart/complete`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ fileId, parts }),
  });

  const txt = await res.text();
  if (!res.ok) throw new Error(txt || `HTTP ${res.status}`);
  return JSON.parse(txt) as { ok: boolean };
}

/**
 * Abort upload:
 * - tells backend to abort multipart upload in S3 and mark session ABORTED in DynamoDB
 *
 * When used:
 * - user cancels
 * - fatal upload error occurs
 */
export async function abortUpload(
  config: AppConfig,
  token: string,
  fileId: string,
) {
  const res = await fetch(`${config.apiBaseUrl}/multipart/abort`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ fileId }),
  });

  const txt = await res.text();
  if (!res.ok) throw new Error(txt || `HTTP ${res.status}`);
  return JSON.parse(txt) as { ok: boolean };
}

/**
 * Variant that returns XHR handle + promise:
 * - Allows callers to cancel an in-flight upload by calling xhr.abort().
 *
 * onLoaded:
 * - absolute loaded bytes for the request (not delta)
 *
 * Gotcha:
 * - This duplicates putPart() conceptually; consider consolidating over time to reduce drift.
 */
export function putPartWithXhr(
  url: string,
  blob: Blob,
  onLoaded: (loadedBytes: number) => void,
): { xhr: XMLHttpRequest; promise: Promise<string> } {
  const xhr = new XMLHttpRequest();

  const promise = new Promise<string>((resolve, reject) => {
    xhr.open("PUT", url, true);

    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) onLoaded(e.loaded);
    };

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300)
        resolve(xhr.getResponseHeader("ETag") || "");
      else reject(new Error(`S3 error ${xhr.status}`));
    };

    xhr.onerror = () => reject(new Error("Network error"));
    xhr.onabort = () => reject(new Error("Aborted"));

    xhr.send(blob);
  });

  return { xhr, promise };
}
